import { useState, useMemo, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import MgmtLayout from '@/components/feature/MgmtLayout';
import {
  useManagementDailyLog,
  updateReviewStatus,
  deletePhoto,
  type DailyLogReviewPhoto,
  type ProjectFilterItem,
  type StageFilterItem,
  type ProfileItem,
} from '@/hooks/useManagementDailyLog';
import { useProjectSummary } from '@/hooks/useProjectSummary';
import ProjectPhotoImage from '@/components/base/ProjectPhotoImage';
import PhotoLightbox, { type LightboxPhoto } from '@/components/feature/PhotoLightbox';
import NewInstructionModal from '@/components/feature/NewInstructionModal';
import { useDailyLogCreate } from '@/hooks/useDailyLogCreate';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/lib/supabase';
import { formatDate } from '@/lib/dateUtils';
import CreateDailyLogModal, { type NewUploadFile, type ExistingPhoto, type DailyLogInitialValues } from '@/pages/management/daily-log/components/CreateDailyLogModal';
import DailyLogReviewModal from '@/components/feature/DailyLogReviewModal';
import PrepareDailyLogForClientModal from '@/components/feature/PrepareDailyLogForClientModal';
import { isPmAdditionalDailyLogPhoto, deletePmDailyLogPhoto } from '@/lib/dailyLogClientShare';
import { resolveFileDisplayUrl, useSignedFileUrls } from '@/lib/storageSignedUrl';
import { normalizeDailyLogId } from '@/lib/dailyLogShareDiagnostics';

export default function ManagementDailyLog() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { photos, projects, stages, profiles, dailyLogs, dailyLogsLoading, loading, error, refresh } = useManagementDailyLog();

  // Build translated status options inside component (reactive to language change)
  const STATUS_OPTIONS = [
    { value: 'all', label: t('dailyLog.allStatuses') },
    { value: 'pending_management_review', label: t('dailyLog.pendingReview') },
    { value: 'approved_for_client', label: t('dailyLog.approvedForClient') },
    { value: 'internal_only', label: t('dailyLog.internalOnlyBadge') },
    { value: 'rejected', label: t('dailyLog.rejected') },
  ];

  const STATUS_BADGE: Record<string, { bg: string; text: string; label: string }> = {
    pending_management_review: { bg: '#FFF7ED', text: '#F97316', label: t('dailyLog.pendingReview') },
    approved_for_client: { bg: '#F0FDF4', text: '#059669', label: t('dailyLog.approvedForClient') },
    internal_only: { bg: '#F3F4F6', text: '#374151', label: t('dailyLog.internalOnlyBadge') },
    rejected: { bg: '#FEF2F2', text: '#EF4444', label: t('dailyLog.rejected') },
  };

  function getStatusBadge(status: string | null) {
    return STATUS_BADGE[status || ''] || { bg: '#F3F4F6', text: '#6B7280', label: status || t('statusLabel.unknown') };
  }

  function groupPhotosByDateProject(
    photoList: DailyLogReviewPhoto[]
  ): Array<{
    dateKey: string;
    dateLabel: string;
    groups: Array<{
      projectId: string;
      projectName: string;
      projectCode: string;
      photos: DailyLogReviewPhoto[];
    }>;
  }> {
    const byDate: Record<string, Record<string, DailyLogReviewPhoto[]>> = {};

    photoList.forEach((p) => {
      const d = p.log_date || p.created_at.split('T')[0];
      if (!byDate[d]) byDate[d] = {};
      if (!byDate[d][p.project_id]) byDate[d][p.project_id] = [];
      byDate[d][p.project_id].push(p);
    });

    const sortedDates = Object.keys(byDate).sort().reverse();

    return sortedDates.map((dateKey) => {
      const groups = Object.entries(byDate[dateKey]).map(([projectId, photosArr]) => ({
        projectId,
        projectName: '',
        projectCode: '',
        photos: photosArr,
      }));
      return {
        dateKey,
        dateLabel: formatDate(dateKey),
        groups: groups.sort((a, b) => b.photos[0].created_at.localeCompare(a.photos[0].created_at)),
      };
    });
  }

  // Filters
  const [filterProject, setFilterProject] = useState<string>('all');
  const [filterStatus, setFilterStatus] = useState<string>('pending_management_review');
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');
  const [filterStage, setFilterStage] = useState<string>('all');
  const [filterSubcontractor, setFilterSubcontractor] = useState<string>('all');
  const [showFilters, setShowFilters] = useState(false);

  // ── LIVE project summary when viewing a single project ──
  const summaryProjectId = filterProject !== 'all' ? filterProject : undefined;
  const projectSummary = useProjectSummary(summaryProjectId);

  // Selection
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkAction, setBulkAction] = useState<'approve' | 'internal' | 'reject' | null>(null);
  const [bulkNote, setBulkNote] = useState('');
  const [bulkLoading, setBulkLoading] = useState(false);
  const [shareReviewOpen, setShareReviewOpen] = useState(false);
  const [shareReviewPhotoIds, setShareReviewPhotoIds] = useState<string[]>([]);
  const [shareReviewNote, setShareReviewNote] = useState('');
  const [prepareClientOpen, setPrepareClientOpen] = useState(false);
  const [prepareClientContext, setPrepareClientContext] = useState<{
    projectId: string;
    projectName: string;
    logDate: string;
    dateLabel: string;
    photos: DailyLogReviewPhoto[];
  } | null>(null);

  // Lightbox
  const [lightboxPhotos, setLightboxPhotos] = useState<LightboxPhoto[]>([]);
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);

  // Single photo note modal
  const [notePhotoId, setNotePhotoId] = useState<string | null>(null);
  const [noteText, setNoteText] = useState('');

  // Toast
  const [toast, setToast] = useState<{message: string; type: 'success'|'error'; action?: { label: string; onClick: () => void } }|null>(null);
  const [deleteId, setDeleteId] = useState<string | null>(null);

  const [showInstructionModal, setShowInstructionModal] = useState(false);
  const [instructionPhotoId, setInstructionPhotoId] = useState<string | undefined>();
  const [instructionPhotoTitle, setInstructionPhotoTitle] = useState('');

  // Daily Log creation / edit state
  const [showDailyLogModal, setShowDailyLogModal] = useState(false);
  const [dailyLogMode, setDailyLogMode] = useState<'create' | 'edit'>('create');
  const [editLogId, setEditLogId] = useState<string | undefined>(undefined);
  const [editInitialValues, setEditInitialValues] = useState<DailyLogInitialValues | undefined>(undefined);
  const [dailyLogFixedProjectId, setDailyLogFixedProjectId] = useState<string | undefined>(undefined);

  // Delete confirmation
  const [deleteLogId, setDeleteLogId] = useState<string | null>(null);
  const [deleteLogTitle, setDeleteLogTitle] = useState('');
  const [deleteError, setDeleteError] = useState('');
  const [deleting, setDeleting] = useState(false);

  const { createDailyLog, updateDailyLog, creating: creatingDailyLog } = useDailyLogCreate();
  const { user } = useAuth();

  async function handleCreateDailyLog(payload: {
    input: Parameters<typeof createDailyLog>[0]['input'];
    newFiles: NewUploadFile[];
    existingPhotoIds: string[];
  }) {
    try {
      const result = await createDailyLog({
        input: payload.input,
        newFiles: payload.newFiles,
        existingPhotoIds: payload.existingPhotoIds,
      });

      if (result.success && result.dailyLogId) {
        setToast({
          message: t('dailyLog.toastCreatedSuccess'),
          type: 'success',
          action: {
            label: t('dailyLog.viewDailyLog'),
            onClick: () => {
              navigate(`/management/projects/${payload.input.project_id}?tab=Daily+Log`);
            },
          },
        });
        setShowDailyLogModal(false);
        setSelectedIds(new Set());
        refresh();
      } else {
        setToast({ message: result.error || t('dailyLog.toastCreateFailed'), type: 'error' });
      }

      return result;
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setToast({ message: msg || t('dailyLog.toastCreateFailed'), type: 'error' });
      return { success: false, error: msg };
    }
  }

  useEffect(() => {
    if (!toast) return;
    // Success toasts with an action stay longer so user can click the link
    const duration = (toast.type === 'success' && toast.action) ? 8000 : 4000;
    const timer = setTimeout(() => setToast(null), duration);
    return () => clearTimeout(timer);
  }, [toast]);

  const projectById = useMemo(() => {
    const map = new Map<string, ProjectFilterItem>();
    projects.forEach((p) => map.set(p.id, p));
    return map;
  }, [projects]);

  const stageById = useMemo(() => {
    const map = new Map<string, StageFilterItem>();
    stages.forEach((s) => map.set(s.id, s));
    return map;
  }, [stages]);

  const profileById = useMemo(() => {
    const map = new Map<string, ProfileItem>();
    profiles.forEach((p) => map.set(p.id, p));
    return map;
  }, [profiles]);

  const activeSubcontractors = useMemo(() => {
    const ids = new Set<string>();
    photos.forEach((p) => {
      if (p.uploaded_by) ids.add(p.uploaded_by);
    });
    return Array.from(ids)
      .map((id) => profileById.get(id))
      .filter(Boolean) as ProfileItem[];
  }, [photos, profileById]);

  const activeStages = useMemo(() => {
    const ids = new Set<string>();
    photos.forEach((p) => {
      if (p.stage_id) ids.add(p.stage_id);
    });
    return Array.from(ids)
      .map((id) => stageById.get(id))
      .filter(Boolean) as StageFilterItem[];
  }, [photos, stageById]);

  const filteredPhotos = useMemo(() => {
    return photos.filter((p) => {
      if (filterProject !== 'all' && p.project_id !== filterProject) return false;
      if (filterStatus !== 'all') {
        const isPmPhoto = isPmAdditionalDailyLogPhoto(p.note);
        const matchesStatus =
          p.review_status === filterStatus ||
          (filterStatus === 'pending_management_review' &&
            isPmPhoto &&
            (p.review_status === 'internal_only' || p.review_status === 'pending_management_review'));
        if (!matchesStatus) return false;
      }
      if (filterStage !== 'all' && p.stage_id !== filterStage) return false;
      if (filterSubcontractor !== 'all' && p.uploaded_by !== filterSubcontractor) return false;
      if (filterDateFrom && p.log_date && p.log_date < filterDateFrom) return false;
      if (filterDateTo && p.log_date && p.log_date > filterDateTo) return false;
      return true;
    });
  }, [photos, filterProject, filterStatus, filterStage, filterSubcontractor, filterDateFrom, filterDateTo]);

  const photoDisplayUrls = useSignedFileUrls(filteredPhotos);

  const grouped = useMemo(() => {
    const raw = groupPhotosByDateProject(filteredPhotos);
    return raw.map((day) => ({
      ...day,
      groups: day.groups.map((g) => ({
        ...g,
        projectName: projectById.get(g.projectId)?.project_name || t('statusLabel.unknown_project'),
        projectCode: projectById.get(g.projectId)?.project_code || '',
      })),
    }));
  }, [filteredPhotos, projectById, t]);

  const stats = useMemo(() => {
    const pending = photos.filter((p) => p.review_status === 'pending_management_review').length;
    const approved = photos.filter((p) => p.review_status === 'approved_for_client').length;
    const internal = photos.filter((p) => p.review_status === 'internal_only').length;
    const rejected = photos.filter((p) => p.review_status === 'rejected').length;
    return { pending, approved, internal, rejected, total: photos.length };
  }, [photos]);

  const toggleSelect = useCallback((id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const clearSelection = useCallback(() => {
    setSelectedIds(new Set());
  }, []);

  async function executeBulkAction() {
    if (!bulkAction || selectedIds.size === 0) return;
    if (bulkAction === 'approve') {
      setShareReviewPhotoIds(Array.from(selectedIds));
      setShareReviewNote(bulkNote.trim());
      setShareReviewOpen(true);
      return;
    }
    setBulkLoading(true);
    try {
      const statusMap: Record<string, 'approved_for_client' | 'internal_only' | 'rejected'> = {
        approve: 'approved_for_client',
        internal: 'internal_only',
        reject: 'rejected',
      };
      await updateReviewStatus(
        Array.from(selectedIds),
        statusMap[bulkAction],
        bulkNote.trim() || undefined
      );
      setSelectedIds(new Set());
      setBulkAction(null);
      setBulkNote('');
      refresh();
    } catch (e) {
      setToast({ message: String(e), type: 'error' });
    } finally {
      setBulkLoading(false);
    }
  }

  async function confirmClientShareReview() {
    if (shareReviewPhotoIds.length === 0) return;
    setBulkLoading(true);
    try {
      await updateReviewStatus(
        shareReviewPhotoIds,
        'approved_for_client',
        shareReviewNote.trim() || undefined
      );
      setShareReviewOpen(false);
      setShareReviewPhotoIds([]);
      setShareReviewNote('');
      setSelectedIds(new Set());
      setBulkAction(null);
      setBulkNote('');
      setToast({ message: t('dailyLog.approvedForClient'), type: 'success' });
      refresh();
    } catch (e) {
      setToast({ message: String(e), type: 'error' });
    } finally {
      setBulkLoading(false);
    }
  }

  async function handleSingleAction(photoId: string, action: 'approve' | 'internal' | 'reject') {
    if (action === 'approve') {
      setShareReviewPhotoIds([photoId]);
      setShareReviewNote('');
      setShareReviewOpen(true);
      return;
    }
    const statusMap = {
      approve: 'approved_for_client' as const,
      internal: 'internal_only' as const,
      reject: 'rejected' as const,
    };
    try {
      await updateReviewStatus([photoId], statusMap[action]);
      refresh();
    } catch (e) {
      setToast({ message: String(e), type: 'error' });
    }
  }

  async function saveNote() {
    if (!notePhotoId) return;
    try {
      await updateReviewStatus([notePhotoId], (photos.find((p) => p.id === notePhotoId)?.review_status as 'approved_for_client' | 'internal_only' | 'rejected') || 'internal_only', noteText.trim() || undefined);
      setNotePhotoId(null);
      setNoteText('');
      refresh();
    } catch (e) {
      setToast({ message: t('dailyLog.failedToSaveNote') + String(e), type: 'error' });
    }
  }

  async function confirmDelete() {
    if (!deleteId) return;
    const photo = photos.find((p) => p.id === deleteId);
    try {
      if (
        photo &&
        isPmAdditionalDailyLogPhoto(photo.note) &&
        photo.daily_log_id
      ) {
        const result = await deletePmDailyLogPhoto({
          fileId: deleteId,
          dailyLogId: photo.daily_log_id,
          projectId: photo.project_id,
        });
        if (!result.ok) {
          throw new Error(result.error || 'Failed to delete PM photo');
        }
      } else {
        await deletePhoto(deleteId);
      }
      setDeleteId(null);
      refresh();
    } catch (e) {
      setToast({ message: t('dailyLog.failedToDelete') + String(e), type: 'error' });
    }
  }

  const openInstructionForPhoto = useCallback((photo: DailyLogReviewPhoto) => {
    setInstructionPhotoId(photo.id);
    setInstructionPhotoTitle(`${t('dailyLog.actionRequired')}${photo.caption || photo.file_name}`);
    setShowInstructionModal(true);
  }, []);

  const openLightbox = useCallback(
    (photoId: string) => {
      const items: LightboxPhoto[] = filteredPhotos.map((p) => ({
        url: photoDisplayUrls[p.id] || resolveFileDisplayUrl(p),
        caption: p.caption || p.file_name,
      }));
      const idx = filteredPhotos.findIndex((p) => p.id === photoId);
      setLightboxPhotos(items);
      setLightboxIndex(idx >= 0 ? idx : 0);
    },
    [filteredPhotos, photoDisplayUrls]
  );

  const uploaderName = (uploadedBy: string | null) => {
    if (!uploadedBy) return t('statusLabel.unknown');
    const prof = profileById.get(uploadedBy);
    return prof?.full_name || prof?.company_name || t('statusLabel.unknown');
  };

  const selectedExistingPhotos: ExistingPhoto[] = useMemo(() => {
    return filteredPhotos
      .filter((p) => selectedIds.has(p.id))
      .map((p) => ({
        id: p.id,
        file_url: p.file_url,
        caption: p.caption,
        file_name: p.file_name,
        uploaded_by_name: uploaderName(p.uploaded_by),
      }));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredPhotos, selectedIds]);

  const pendingCount = filteredPhotos.filter(
    (p) => p.review_status === 'pending_management_review'
  ).length;

  async function handleEditDailyLog(payload: {
    input: Parameters<typeof updateDailyLog>[0]['input'] & { logId?: string };
    newFiles: NewUploadFile[];
  }) {
    try {
      const resolvedEditId = normalizeDailyLogId(editLogId) ?? normalizeDailyLogId(payload.input.logId);
      if (!resolvedEditId) {
        const msg = 'Daily Log ID could not be resolved. The update was not sent.';
        console.warn('[ManagementDailyLog] handleEditDailyLog blocked: invalid editLogId', {
          editLogId,
          payloadLogId: payload.input.logId,
        });
        setToast({ message: msg, type: 'error' });
        return { success: false, error: msg };
      }

      const { logId: _ignoredLogId, ...inputFields } = payload.input;
      const result = await updateDailyLog({
        dailyLogId: resolvedEditId,
        input: inputFields,
        newFiles: payload.newFiles,
        existingPhotoIds: [],
        removedLogPhotoIds: [],
      });
      if (result.success) {
        setToast({ message: 'Daily Log updated successfully', type: 'success' });
        setShowDailyLogModal(false);
        setEditLogId(undefined);
        setEditInitialValues(undefined);
        refresh();
      } else {
        setToast({ message: result.error || 'Failed to update Daily Log', type: 'error' });
      }
      return result;
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setToast({ message: msg || 'Failed to update Daily Log', type: 'error' });
      return { success: false, error: msg };
    }
  }

  async function confirmDeleteLog() {
    if (!deleteLogId) return;
    setDeleting(true);
    setDeleteError('');
    try {
      const { error } = await supabase
        .from('jphph_daily_logs')
        .update({
          is_deleted: true,
          deleted_at: new Date().toISOString(),
          deleted_by: user?.id || null,
        })
        .eq('id', deleteLogId);
      if (error) throw error;
      setToast({ message: 'Daily Log archived', type: 'success' });
      setDeleteLogId(null);
      refresh();
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setDeleteError(msg || 'Failed to archive Daily Log');
    } finally {
      setDeleting(false);
    }
  }

  function openEditModal(log: (typeof dailyLogs)[0]) {
    setDailyLogMode('edit');
    setEditLogId(log.id);
    setEditInitialValues({
      logId: log.id,
      projectId: log.project_id,
      logDate: log.log_date,
      title: log.title || '',
      managementComment: log.management_comment || '',
      internalNote: log.internal_note || '',
      visibility: (log.visibility as 'internal' | 'client') || 'client',
      isPublishedToClient: log.is_published_to_client,
    });
    setDailyLogFixedProjectId(log.project_id);
    setShowDailyLogModal(true);
  }
  // ── Active filter summary text ──
  const activeFilterSummary = useMemo(() => {
    const parts: string[] = [];
    if (filterProject !== 'all') {
      const p = projects.find((pr) => pr.id === filterProject);
      parts.push(`Project: ${p?.project_name || filterProject}`);
    }
    if (filterStatus !== 'all') {
      parts.push(`Status: ${STATUS_OPTIONS.find((s) => s.value === filterStatus)?.label || filterStatus}`);
    }
    if (filterStage !== 'all') {
      const s = stages.find((st) => st.id === filterStage);
      parts.push(`Stage: ${s?.stage_name || filterStage}`);
    }
    if (filterSubcontractor !== 'all') {
      const sub = profiles.find((pr) => pr.id === filterSubcontractor);
      parts.push(`Sub: ${sub?.full_name || sub?.company_name || filterSubcontractor}`);
    }
    if (filterDateFrom) parts.push(`From: ${filterDateFrom}`);
    if (filterDateTo) parts.push(`To: ${filterDateTo}`);
    return parts;
  }, [filterProject, filterStatus, filterStage, filterSubcontractor, filterDateFrom, filterDateTo, projects, stages, profiles, STATUS_OPTIONS]);

  const shareReviewPhotos = useMemo(() => {
    return photos
      .filter((p) => shareReviewPhotoIds.includes(p.id))
      .map((p) => ({
        id: p.id,
        url: p.file_url,
        caption: p.caption || p.note || p.file_name,
        fileName: p.file_name,
      }));
  }, [photos, shareReviewPhotoIds]);

  const shareReviewProjects = useMemo(() => {
    const names = new Set(
      photos
        .filter((p) => shareReviewPhotoIds.includes(p.id))
        .map((p) => projectById.get(p.project_id)?.project_name)
        .filter(Boolean)
    );
    return Array.from(names).join(', ');
  }, [photos, shareReviewPhotoIds, projectById]);

  return (
    <MgmtLayout title={t('dailyLog.title')}>
      {/* Toast */}
      {toast && (
        <div className={`fixed top-20 right-4 z-[100] px-4 py-2.5 rounded-xl text-sm font-semibold shadow-lg transition-all max-w-xs ${
          toast.type === 'success' ? 'bg-[#059669] text-white' : 'bg-[#EF4444] text-white'
        }`}>
          <div className="flex items-center gap-2">
            <i className={toast.type === 'success' ? 'ri-check-line' : 'ri-error-warning-line'} />
            <span>{toast.message}</span>
          </div>
          {toast.action && (
            <button
              onClick={toast.action.onClick}
              className="mt-1.5 text-xs font-bold underline hover:no-underline cursor-pointer whitespace-nowrap"
            >
              {toast.action.label} <i className="ri-arrow-right-line" />
            </button>
          )}
        </div>
      )}

      {/* Lightbox */}
      {lightboxIndex !== null && lightboxPhotos.length > 0 && (
        <PhotoLightbox
          photos={lightboxPhotos}
          initialIndex={lightboxIndex}
          onClose={() => {
            setLightboxIndex(null);
            setLightboxPhotos([]);
          }}
        />
      )}

      {/* Delete confirm */}
      {deleteId && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center px-4">
          <div className="absolute inset-0 bg-black/50" onClick={() => setDeleteId(null)} />
          <div className="relative bg-white w-full max-w-sm rounded-2xl p-6 text-center">
            <div className="w-12 h-12 rounded-2xl bg-[#FEF2F2] flex items-center justify-center mx-auto mb-3">
              <i className="ri-delete-bin-line text-[#EF4444] text-2xl" />
            </div>
            <p className="text-[#1A1A1A] font-bold text-base mb-1">{t('dailyLog.removePhoto')}</p>
            <p className="text-[#6B7280] text-sm mb-5">{t('dailyLog.removePhotoDesc')}</p>
            <div className="flex gap-3">
              <button onClick={() => setDeleteId(null)} className="flex-1 py-3 rounded-xl bg-gray-100 text-[#374151] text-sm font-semibold cursor-pointer whitespace-normal sm:whitespace-nowrap">
                {t('dailyLog.cancel')}
              </button>
              <button onClick={confirmDelete} className="flex-1 py-3 rounded-xl bg-[#EF4444] text-white text-sm font-semibold cursor-pointer whitespace-normal sm:whitespace-nowrap">
                {t('dailyLog.remove')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Note modal */}
      {notePhotoId && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center px-4">
          <div className="absolute inset-0 bg-black/50" onClick={() => setNotePhotoId(null)} />
          <div className="relative bg-white w-full max-w-sm rounded-2xl p-5">
            <h3 className="text-[#1A1A1A] font-bold text-sm mb-3">{t('dailyLog.managementNoteLabel')}</h3>
            <textarea
              value={noteText}
              onChange={(e) => setNoteText(e.target.value)}
              placeholder={t('dailyLog.addNote')}
              rows={3}
              maxLength={500}
              className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm text-[#1A1A1A] placeholder-[#9CA3AF] resize-none focus:outline-none focus:border-[#E8621A]"
            />
            <div className="flex justify-end gap-2 mt-3">
              <button onClick={() => setNotePhotoId(null)} className="px-4 py-2 rounded-xl bg-gray-100 text-[#374151] text-sm font-medium cursor-pointer whitespace-normal sm:whitespace-nowrap">
                {t('dailyLog.cancel')}
              </button>
              <button onClick={saveNote} className="px-4 py-2 rounded-xl bg-[#E8621A] text-white text-sm font-semibold cursor-pointer whitespace-normal sm:whitespace-nowrap">
                {t('dailyLog.saveNote')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk action bar — now also shows Create Daily Log button when no selection */}
      {selectedIds.size > 0 && (
        <div className="sticky top-[64px] z-30 bg-white border-b border-gray-100 px-4 lg:px-8 py-3 flex flex-wrap items-center gap-3">
          <span className="text-sm font-semibold text-[#1A1A1A]">
            {selectedIds.size} {t('dailyLog.selected')}
          </span>
          <div className="flex items-center gap-2 flex-wrap">
            <button
              onClick={() => setBulkAction('approve')}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold cursor-pointer whitespace-nowrap transition-colors ${bulkAction === 'approve' ? 'bg-[#059669] text-white' : 'bg-[#F0FDF4] text-[#059669] border border-[#059669]/20'}`}
            >
              <i className="ri-check-line mr-1" />{t('dailyLog.approveForClient')}
            </button>
            <button
              onClick={() => setBulkAction('internal')}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold cursor-pointer whitespace-nowrap transition-colors ${bulkAction === 'internal' ? 'bg-[#374151] text-white' : 'bg-[#F3F4F6] text-[#374151] border border-gray-200'}`}
            >
              <i className="ri-eye-off-line mr-1" />{t('dailyLog.keepInternal')}
            </button>
            <button
              onClick={() => setBulkAction('reject')}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold cursor-pointer whitespace-nowrap transition-colors ${bulkAction === 'reject' ? 'bg-[#EF4444] text-white' : 'bg-[#FEF2F2] text-[#EF4444] border border-[#EF4444]/20'}`}
            >
              <i className="ri-close-line mr-1" />{t('dailyLog.reject')}
            </button>
          </div>
          {bulkAction && (
            <div className="flex items-center gap-2 flex-wrap ml-auto">
              <input
                type="text"
                value={bulkNote}
                onChange={(e) => setBulkNote(e.target.value)}
                placeholder={t('dailyLog.optionalNote')}
                className="text-xs border border-gray-200 rounded-lg px-2 py-1 focus:outline-none focus:border-[#E8621A] w-40"
                maxLength={200}
              />
              <button onClick={executeBulkAction} disabled={bulkLoading} className="px-3 py-1.5 rounded-lg bg-[#E8621A] text-white text-xs font-semibold cursor-pointer whitespace-nowrap disabled:opacity-50">
                {bulkLoading ? t('dailyLog.applying') : t('dailyLog.apply')}
              </button>
              <button onClick={() => { setBulkAction(null); setBulkNote(''); }} className="px-3 py-1.5 rounded-lg bg-gray-100 text-[#374151] text-xs font-medium cursor-pointer whitespace-nowrap">
                {t('dailyLog.cancel')}
              </button>
            </div>
          )}
          {!bulkAction && (
            <>
              <button
                onClick={() => {
                  if (filterProject !== 'all') setDailyLogFixedProjectId(filterProject);
                  else setDailyLogFixedProjectId(undefined);
                  setDailyLogMode('create');
                  setEditLogId(undefined);
                  setEditInitialValues(undefined);
                  setShowDailyLogModal(true);
                }}
                className="px-3 py-1.5 rounded-lg bg-[#059669] text-white text-xs font-semibold cursor-pointer whitespace-nowrap"
              >
                <i className="ri-book-open-line mr-1" />{t('dailyLog.createDailyLog')}
              </button>
              <button onClick={clearSelection} className="ml-auto text-[#9CA3AF] text-xs font-medium hover:text-[#374151] cursor-pointer whitespace-nowrap">
                {t('dailyLog.clearSelection')}
              </button>
            </>
          )}
        </div>
      )}

      <div className="px-4 lg:px-8 pt-4 pb-10 overflow-x-hidden">
        {/* Header row with Create + Reload */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <i className="ri-book-open-line text-[#059669]" />
            <span className="text-sm font-bold text-[#1A1A1A]">{t('dailyLog.title')}</span>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={refresh}
              className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-white border border-gray-200 text-[#374151] text-xs font-semibold cursor-pointer hover:bg-gray-50 transition-colors whitespace-nowrap"
              title={t('btn.reload')}
            >
              <i className="ri-refresh-line" />
              Reload
            </button>
            <button
              onClick={() => {
                if (filterProject !== 'all') setDailyLogFixedProjectId(filterProject);
                else setDailyLogFixedProjectId(undefined);
                setDailyLogMode('create');
                setEditLogId(undefined);
                setEditInitialValues(undefined);
                setShowDailyLogModal(true);
              }}
              className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-[#059669] text-white text-xs font-semibold cursor-pointer hover:bg-[#047857] transition-colors whitespace-nowrap"
            >
              <i className="ri-add-line" />
              {t('dailyLog.createDailyLog')}
            </button>
          </div>
        </div>

        {/* ── Single-project live status banner ── */}
        {filterProject !== 'all' && projectSummary.loaded && (
          <div className="mb-4 p-4 bg-white border border-gray-100 rounded-xl">
            <div className="flex items-center justify-between gap-4">
              <div className="min-w-0">
                <p className="text-[#9CA3AF] text-[10px] font-bold uppercase tracking-wider">Current Project Status</p>
                <div className="flex items-center gap-2 mt-1 flex-wrap">
                  <span
                    className="text-[10px] font-bold px-2 py-0.5 rounded-full capitalize"
                    style={{
                      backgroundColor: projectSummary.projectStatus === 'complete' ? '#F0FDF4' : '#FEF0E7',
                      color: projectSummary.projectStatus === 'complete' ? '#059669' : '#E8621A',
                    }}
                  >
                    {projectSummary.projectStatus === 'complete' ? 'Completed' : projectSummary.projectStatus}
                  </span>
                  <span
                    className="text-[10px] font-bold px-2 py-0.5 rounded-full"
                    style={{
                      backgroundColor: projectSummary.activeStage === 'Completed' ? '#F0FDF4' : '#FEF0E7',
                      color: projectSummary.activeStage === 'Completed' ? '#059669' : '#E8621A',
                    }}
                  >
                    {projectSummary.activeStage}
                  </span>
                  <span className="text-[10px] text-[#9CA3AF]">
                    {projectSummary.projectProgress}% complete
                  </span>
                </div>
              </div>
              <div className="flex gap-3 text-right flex-shrink-0">
                <div>
                  <div className="text-[#1A1A1A] font-bold text-sm">{projectSummary.projectProgress}%</div>
                  <div className="text-[#9CA3AF] text-[10px]">Project</div>
                </div>
                <div>
                  <div className="text-[#1A1A1A] font-bold text-sm">{projectSummary.scheduleFinalProgress}%</div>
                  <div className="text-[#9CA3AF] text-[10px]">Schedule</div>
                </div>
                <div>
                  <div className="text-[#1A1A1A] font-bold text-sm">{projectSummary.checklistFinalProgress}%</div>
                  <div className="text-[#9CA3AF] text-[10px]">Checklist</div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Stats bar */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-4">
          {[
            { label: t('dailyLog.pendingReview'), value: stats.pending, color: '#F97316', bg: '#FFF7ED' },
            { label: t('dailyLog.approvedForClient'), value: stats.approved, color: '#059669', bg: '#F0FDF4' },
            { label: t('dailyLog.internalOnlyBadge'), value: stats.internal, color: '#374151', bg: '#F3F4F6' },
            { label: t('dailyLog.rejected'), value: stats.rejected, color: '#EF4444', bg: '#FEF2F2' },
          ].map((s) => (
            <div key={s.label} className="rounded-xl p-3 border border-gray-100" style={{ backgroundColor: s.bg }}>
              <p className="text-xl font-bold" style={{ color: s.color }}>{s.value}</p>
              <p className="text-[11px] font-medium mt-0.5" style={{ color: s.color }}>{s.label}</p>
            </div>
          ))}
        </div>

        {/* Created Daily Logs */}
        {dailyLogs.length > 0 && (
          <div className="bg-white rounded-2xl border border-gray-100 p-4 mb-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <i className="ri-book-open-line text-[#059669]" />
                <span className="text-sm font-semibold text-[#1A1A1A]">{t('dailyLog.createdDailyLogs')}</span>
                <span className="px-2 py-0.5 bg-[#F0FDF4] text-[#059669] text-[10px] font-bold rounded-full">
                  {dailyLogs.length}
                </span>
              </div>
            </div>
            <div className="space-y-2 max-h-[280px] overflow-y-auto pr-1">
              {dailyLogs.slice(0, 20).map((log) => {
                const proj = projectById.get(log.project_id);
                const uploader = log.created_by ? (profileById.get(log.created_by)?.full_name || profileById.get(log.created_by)?.company_name || t('statusLabel.unknown')) : t('statusLabel.unknown');
                return (
                  <div
                    key={log.id}
                    className="flex items-center justify-between p-3 rounded-xl border border-gray-100 hover:border-[#E8621A]/30 transition-colors bg-[#FAFAFA]"
                  >
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-semibold text-[#1A1A1A] truncate">
                          {log.title || t('dailyLog.untitledDailyLog')}
                        </span>
                        {log.is_published_to_client && (
                          <span className="px-1.5 py-0.5 bg-[#F0FDF4] text-[#059669] text-[9px] font-bold rounded-full">
                            {t('dailyLog.clientVisible')}
                          </span>
                        )}
                        {!log.is_published_to_client && (
                          <span className="px-1.5 py-0.5 bg-[#F3F4F6] text-[#374151] text-[9px] font-bold rounded-full">
                            {t('dailyLog.internalOnlyBadge')}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-2 mt-1 flex-wrap">
                        <span className="text-[10px] text-[#9CA3AF]">{formatDate(log.log_date)}</span>
                        <span className="text-[10px] text-[#E8621A] font-medium">
                          {proj?.project_name || t('statusLabel.unknown_project')}
                        </span>
                        {log.management_comment && (
                          <span className="text-[10px] text-[#6B7280] truncate max-w-[200px]">
                            {log.management_comment}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 ml-3 flex-shrink-0">
                      <button
                        type="button"
                        onClick={() => openEditModal(log)}
                        className="px-3 py-1.5 rounded-lg bg-white border border-gray-200 text-[#374151] text-[10px] font-bold cursor-pointer whitespace-nowrap hover:bg-gray-50 transition-colors"
                        title="Edit"
                      >
                        <i className="ri-edit-line" />
                      </button>
                      <button
                        onClick={() => { setDeleteLogId(log.id); setDeleteLogTitle(log.title || t('dailyLog.untitledDailyLog')); setDeleteError(''); }}
                        className="px-3 py-1.5 rounded-lg bg-white border border-gray-200 text-[#EF4444] text-[10px] font-bold cursor-pointer whitespace-nowrap hover:bg-[#FEF2F2] transition-colors"
                        title="Archive"
                      >
                        <i className="ri-delete-bin-line" />
                      </button>
                      <button
                        onClick={() => navigate(`/management/projects/${log.project_id}?tab=Daily+Log`)}
                        className="px-3 py-1.5 rounded-lg bg-[#E8621A] text-white text-[10px] font-bold cursor-pointer whitespace-nowrap hover:bg-[#B84C10] transition-colors"
                      >
                        {t('dailyLog.view')}
                      </button>
                      <button
                        onClick={() => navigate(`/client/portal?projectId=${log.project_id}&tab=Daily+Log`)}
                        className="px-3 py-1.5 rounded-lg bg-white border border-gray-200 text-[#374151] text-[10px] font-bold cursor-pointer whitespace-nowrap hover:bg-gray-50 transition-colors"
                        title={t('dailyLog.previewAsClient')}
                      >
                        <i className="ri-eye-line" />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {dailyLogsLoading && (
          <div className="flex items-center gap-2 text-[#9CA3AF] text-xs mb-4 px-1">
            <i className="ri-loader-4-line animate-spin" />
            {t('dailyLog.loadingDailyLogs')}
          </div>
        )}

        {/* Filters */}
        <div className="bg-white rounded-2xl border border-gray-100 p-4 mb-4">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <i className="ri-filter-3-line text-[#9CA3AF]" />
              <span className="text-sm font-semibold text-[#1A1A1A]">{t('dailyLog.filters')}</span>
              {pendingCount > 0 && filterStatus === 'pending_management_review' && (
                <span className="px-2 py-0.5 bg-[#FFF7ED] text-[#F97316] text-[10px] font-bold rounded-full">
                  {pendingCount} {t('dailyLog.pending')}
                </span>
              )}
            </div>
            <button onClick={() => setShowFilters((v) => !v)} className="text-[#9CA3AF] text-xs font-medium hover:text-[#374151] cursor-pointer whitespace-nowrap">
              {showFilters ? t('dailyLog.hide') : t('dailyLog.showAll')}
            </button>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <select value={filterProject} onChange={(e) => setFilterProject(e.target.value)} className="w-full text-xs border border-gray-200 rounded-lg px-2.5 py-2 bg-white text-[#374151] focus:outline-none focus:border-[#E8621A] cursor-pointer">
              <option value="all">{t('dailyLog.allProjects')}</option>
              {projects.map((p) => <option key={p.id} value={p.id}>{p.project_name}</option>)}
            </select>

            <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} className="w-full text-xs border border-gray-200 rounded-lg px-2.5 py-2 bg-white text-[#374151] focus:outline-none focus:border-[#E8621A] cursor-pointer">
              {STATUS_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>

            {activeStages.length > 0 && (
              <select value={filterStage} onChange={(e) => setFilterStage(e.target.value)} className="w-full text-xs border border-gray-200 rounded-lg px-2.5 py-2 bg-white text-[#374151] focus:outline-none focus:border-[#E8621A] cursor-pointer">
                <option value="all">{t('dailyLog.allStages')}</option>
                {activeStages.map((s) => <option key={s.id} value={s.id}>{s.stage_name}</option>)}
              </select>
            )}

            {activeSubcontractors.length > 0 && (
              <select value={filterSubcontractor} onChange={(e) => setFilterSubcontractor(e.target.value)} className="w-full text-xs border border-gray-200 rounded-lg px-2.5 py-2 bg-white text-[#374151] focus:outline-none focus:border-[#E8621A] cursor-pointer">
                <option value="all">{t('dailyLog.allSubcontractors')}</option>
                {activeSubcontractors.map((p) => <option key={p.id} value={p.id}>{p.full_name || p.company_name || t('statusLabel.unknown')}</option>)}
              </select>
            )}

            {showFilters && (
              <>
                <input type="date" value={filterDateFrom} onChange={(e) => setFilterDateFrom(e.target.value)} className="w-full text-xs border border-gray-200 rounded-lg px-2.5 py-2 bg-white text-[#374151] focus:outline-none focus:border-[#E8621A]" />
                <input type="date" value={filterDateTo} onChange={(e) => setFilterDateTo(e.target.value)} className="w-full text-xs border border-gray-200 rounded-lg px-2.5 py-2 bg-white text-[#374151] focus:outline-none focus:border-[#E8621A]" />
              </>
            )}

            <div className="sm:col-span-2 flex items-center justify-between flex-wrap gap-2">
              <button
                onClick={() => { setFilterProject('all'); setFilterStatus('pending_management_review'); setFilterStage('all'); setFilterSubcontractor('all'); setFilterDateFrom(''); setFilterDateTo(''); }}
                className="text-xs text-[#9CA3AF] hover:text-[#374151] cursor-pointer whitespace-nowrap px-2 py-1.5"
              >
                {t('dailyLog.reset')}
              </button>
              {/* Active filter chips */}
              {activeFilterSummary.length > 0 && (
                <div className="flex items-center gap-1.5 flex-wrap">
                  {activeFilterSummary.map((text, i) => (
                    <span key={i} className="px-2 py-0.5 bg-[#FEF0E7] text-[#E8621A] text-[10px] font-bold rounded-full">
                      {text}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Loading */}
        {loading && (
          <div className="flex items-center justify-center py-16">
            <i className="ri-loader-4-line animate-spin text-[#E8621A] text-2xl" />
          </div>
        )}

        {/* Error */}
        {error && !loading && (
          <div className="bg-[#FEF2F2] rounded-xl p-4 text-center">
            <p className="text-[#EF4444] text-sm">{error}</p>
            <button onClick={refresh} className="mt-2 text-[#EF4444] text-xs font-semibold cursor-pointer whitespace-nowrap">
              {t('dailyLog.retry')}
            </button>
          </div>
        )}

        {/* Empty */}
        {!loading && !error && filteredPhotos.length === 0 && (
          <div className="text-center py-16">
            <div className="w-14 h-14 flex items-center justify-center mx-auto mb-3 text-[#D1D5DB]">
              <i className="ri-image-line text-4xl" />
            </div>
            <p className="text-[#374151] font-semibold text-sm">{t('dailyLog.noPhotosMatch')}</p>
            <p className="text-[#9CA3AF] text-xs mt-1">
              {photos.length > 0 ? t('dailyLog.tryAdjusting') : t('dailyLog.noPhotosYet')}
            </p>
            {/* Filter summary + reset when hidden by filters */}
            {photos.length > 0 && activeFilterSummary.length > 0 && (
              <div className="mt-3 flex flex-col items-center gap-2">
                <div className="flex items-center gap-1.5 flex-wrap justify-center">
                  {activeFilterSummary.map((text, i) => (
                    <span key={i} className="px-2 py-0.5 bg-[#FEF0E7] text-[#E8621A] text-[10px] font-bold rounded-full">
                      {text}
                    </span>
                  ))}
                </div>
                <button
                  onClick={() => { setFilterProject('all'); setFilterStatus('all'); setFilterStage('all'); setFilterSubcontractor('all'); setFilterDateFrom(''); setFilterDateTo(''); }}
                  className="text-xs text-[#E8621A] font-semibold underline hover:no-underline cursor-pointer whitespace-nowrap"
                >
                  Clear all filters to show everything
                </button>
              </div>
            )}
          </div>
        )}

        {/* Photo groups by date */}
        {!loading && !error && grouped.map((day) => (
          <div key={day.dateKey} className="mb-6">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-[#1A1A1A] font-bold text-sm">{day.dateLabel}</h3>
              <button
                onClick={() => {
                  const dayIds = day.groups.flatMap((g) => g.photos.map((p) => p.id));
                  const allSelected = dayIds.every((id) => selectedIds.has(id));
                  if (allSelected) {
                    setSelectedIds((prev) => { const next = new Set(prev); dayIds.forEach((id) => next.delete(id)); return next; });
                  } else {
                    setSelectedIds((prev) => { const next = new Set(prev); dayIds.forEach((id) => next.add(id)); return next; });
                  }
                }}
                className="text-[#9CA3AF] text-xs font-medium hover:text-[#374151] cursor-pointer whitespace-nowrap"
              >
                {day.groups.flatMap((g) => g.photos).every((p) => selectedIds.has(p.id)) ? t('dailyLog.deselectAll') : t('dailyLog.selectAll')}
              </button>
            </div>

            {day.groups.map((group) => (
              <div key={group.projectId} className="mb-4">
                <div className="flex items-center gap-2 mb-2 flex-wrap">
                  <button onClick={() => navigate(`/management/projects/${group.projectId}?tab=Photos`)} className="text-[#E8621A] text-xs font-bold hover:underline cursor-pointer whitespace-nowrap">
                    {group.projectName}
                  </button>
                  <span className="text-[#9CA3AF] text-[10px]">{group.projectCode}</span>
                  {group.photos.some((p) => p.review_status === 'pending_management_review') && (
                    <button
                      type="button"
                      onClick={() => {
                        setPrepareClientContext({
                          projectId: group.projectId,
                          projectName: group.projectName,
                          logDate: day.dateKey,
                          dateLabel: day.dateLabel,
                          photos: group.photos,
                        });
                        setPrepareClientOpen(true);
                      }}
                      className="ml-auto px-3 py-1.5 rounded-lg bg-[#059669] text-white text-[10px] font-bold cursor-pointer whitespace-nowrap hover:bg-[#047857] transition-colors"
                    >
                      <i className="ri-share-forward-line mr-0.5" />
                      Prepare for Client
                    </button>
                  )}
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
                  {group.photos.map((photo) => {
                    const badge = getStatusBadge(photo.review_status);
                    const isSelected = selectedIds.has(photo.id);
                    const stageName = photo.stage_id ? stageById.get(photo.stage_id)?.stage_name : null;

                    return (
                      <div key={photo.id} className={`border rounded-xl overflow-hidden bg-white transition-all ${isSelected ? 'border-[#E8621A] ring-1 ring-[#E8621A]' : 'border-gray-100'}`}>
                        <div className="relative aspect-[4/3]">
                          <button onClick={() => openLightbox(photo.id)} className="w-full h-full cursor-pointer block">
                            <ProjectPhotoImage
                              src={photoDisplayUrls[photo.id] || resolveFileDisplayUrl(photo)}
                              alt={photo.caption || photo.file_name}
                              sizes="(max-width: 640px) 50vw, 25vw"
                              loading="lazy"
                              debugContext="ManagementDailyLog"
                            />
                          </button>

                          <div className="absolute top-2 left-2 z-10">
                            <button
                              onClick={() => toggleSelect(photo.id)}
                              className={`w-6 h-6 rounded-lg flex items-center justify-center border-2 cursor-pointer transition-colors ${isSelected ? 'bg-[#E8621A] border-[#E8621A] text-white' : 'bg-white/90 border-white/50 text-[#9CA3AF] hover:border-[#E8621A]'}`}
                            >
                              {isSelected && <i className="ri-check-line text-xs" />}
                            </button>
                          </div>

                          <div className="absolute top-2 right-2 z-10 flex flex-col items-end gap-1">
                            {isPmAdditionalDailyLogPhoto(photo.note) && (
                              <span className="px-2 py-0.5 rounded-full text-[9px] font-bold bg-[#059669] text-white">
                                PM photo
                              </span>
                            )}
                            <span className="px-2 py-0.5 rounded-full text-[9px] font-bold" style={{ backgroundColor: badge.bg, color: badge.text }}>
                              {badge.label}
                            </span>
                          </div>

                          {photo.is_client_visible && (
                            <div className="absolute bottom-2 right-2 z-10">
                              <span className="px-2 py-0.5 bg-[#059669] text-white rounded-full text-[9px] font-bold">
                                {t('dailyLog.clientVisible')}
                              </span>
                            </div>
                          )}
                        </div>

                        <div className="p-2.5">
                          <p className="text-[11px] text-[#374151] font-medium truncate">{photo.caption || photo.file_name}</p>
                          {photo.note && (
                            <p className="text-[10px] text-[#9CA3AF] mt-0.5 line-clamp-1 italic">{t('dailyLog.notePrefix')} {photo.note}</p>
                          )}
                          <div className="flex items-center justify-between mt-1.5">
                            <span className="text-[9px] text-[#9CA3AF]">{uploaderName(photo.uploaded_by)}</span>
                            <span className="text-[9px] text-[#9CA3AF]">
                              {new Date(photo.created_at).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })}
                            </span>
                          </div>
                          {stageName && (
                            <span className="inline-block mt-1 px-1.5 py-0.5 bg-[#FEF0E7] text-[#E8621A] text-[9px] font-bold rounded-full">{stageName}</span>
                          )}

                          <div className="flex items-center gap-1 mt-2 pt-2 border-t border-gray-50">
                            {photo.review_status !== 'approved_for_client' && (
                              <button onClick={() => handleSingleAction(photo.id, 'approve')} className="flex-1 py-1 rounded-lg bg-[#F0FDF4] text-[#059669] text-[10px] font-bold cursor-pointer whitespace-nowrap hover:bg-[#059669]/10 transition-colors">
                                <i className="ri-check-line mr-0.5" />{t('dailyLog.approve')}
                              </button>
                            )}
                            {photo.review_status !== 'internal_only' && (
                              <button onClick={() => handleSingleAction(photo.id, 'internal')} className="flex-1 py-1 rounded-lg bg-[#F3F4F6] text-[#374151] text-[10px] font-bold cursor-pointer whitespace-nowrap hover:bg-gray-200 transition-colors">
                                <i className="ri-eye-off-line mr-0.5" />{t('dailyLog.internal')}
                              </button>
                            )}
                            {photo.review_status !== 'rejected' && (
                              <button onClick={() => handleSingleAction(photo.id, 'reject')} className="flex-1 py-1 rounded-lg bg-[#FEF2F2] text-[#EF4444] text-[10px] font-bold cursor-pointer whitespace-nowrap hover:bg-[#EF4444]/10 transition-colors">
                                <i className="ri-close-line mr-0.5" />{t('dailyLog.reject')}
                              </button>
                            )}
                            <button onClick={() => openInstructionForPhoto(photo)} className="px-2 py-1 rounded-lg bg-[#ECFDF5] text-[#059669] text-[10px] font-bold cursor-pointer whitespace-nowrap hover:bg-[#059669]/10 transition-colors" title={t('dailyLog.instruction')}>
                              <i className="ri-send-plane-line mr-0.5" />{t('dailyLog.instruction')}
                            </button>
                            <button onClick={() => { setNotePhotoId(photo.id); setNoteText(photo.note || ''); }} className="px-2 py-1 rounded-lg bg-gray-50 text-[#9CA3AF] text-[10px] font-bold cursor-pointer whitespace-nowrap hover:bg-gray-100 transition-colors">
                              <i className="ri-sticky-note-line" />
                            </button>
                            <button onClick={() => setDeleteId(photo.id)} className="px-2 py-1 rounded-lg bg-gray-50 text-[#9CA3AF] text-[10px] font-bold cursor-pointer whitespace-nowrap hover:bg-[#FEF2F2] hover:text-[#EF4444] transition-colors">
                              <i className="ri-delete-bin-line" />
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        ))}
      </div>

      {/* Instruction Create Modal */}
      {showInstructionModal && (
        <NewInstructionModal
          show={showInstructionModal}
          onClose={() => setShowInstructionModal(false)}
          projectId={filteredPhotos.find((p) => p.id === instructionPhotoId)?.project_id || ''}
          linkedPhotoId={instructionPhotoId}
          defaultTitle={instructionPhotoTitle}
          source="daily-log"
          onSuccess={() => setShowInstructionModal(false)}
        />
      )}

      {/* Delete Daily Log confirm */}
      {deleteLogId && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center px-4">
          <div className="absolute inset-0 bg-black/50" onClick={() => setDeleteLogId(null)} />
          <div className="relative bg-white w-full max-w-sm rounded-2xl p-6 text-center">
            <div className="w-12 h-12 rounded-2xl bg-[#FEF2F2] flex items-center justify-center mx-auto mb-3">
              <i className="ri-archive-line text-[#EF4444] text-2xl" />
            </div>
            <p className="text-[#1A1A1A] font-bold text-base mb-1">Archive Daily Log</p>
            <p className="text-[#6B7280] text-sm mb-1">
              "{deleteLogTitle}" will be archived and hidden from active lists.
            </p>
            {deleteError && (
              <div className="flex items-start gap-2 bg-[#FEF2F2] rounded-xl px-3 py-2.5 mt-3 text-left">
                <i className="ri-error-warning-line text-[#EF4444] text-sm flex-shrink-0 mt-0.5" />
                <p className="text-[#EF4444] text-xs">{deleteError}</p>
              </div>
            )}
            <div className="flex gap-3 mt-5">
              <button onClick={() => setDeleteLogId(null)} className="flex-1 py-3 rounded-xl bg-gray-100 text-[#374151] text-sm font-semibold cursor-pointer whitespace-nowrap">
                Cancel
              </button>
              <button onClick={confirmDeleteLog} disabled={deleting} className="flex-1 py-3 rounded-xl bg-[#EF4444] text-white text-sm font-semibold cursor-pointer whitespace-nowrap disabled:opacity-50">
                {deleting ? <i className="ri-loader-4-line animate-spin" /> : 'Archive'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Daily Log Modal (create + edit) */}
      <CreateDailyLogModal
        open={showDailyLogModal}
        onClose={() => {
          setShowDailyLogModal(false);
          if (dailyLogMode === 'edit') {
            setEditLogId(undefined);
            setEditInitialValues(undefined);
            setDailyLogMode('create');
          }
        }}
        mode={dailyLogMode}
        editLogId={editLogId}
        initialValues={editInitialValues}
        fixedProjectId={dailyLogFixedProjectId}
        projects={projects}
        existingPhotos={selectedExistingPhotos}
        onRemoveExistingPhoto={(id) => toggleSelect(id)}
        onSubmit={handleCreateDailyLog}
        onSubmitEdit={handleEditDailyLog}
        loading={creatingDailyLog}
      />

      <DailyLogReviewModal
        open={shareReviewOpen}
        title="Review Before Sharing with Client"
        subtitle="These photos will be marked approved for client visibility."
        projectName={shareReviewProjects || undefined}
        bodyTitle="Management review"
        bodyText={shareReviewNote || 'No additional note.'}
        photos={shareReviewPhotos}
        visibilityLabel="Approved for client"
        submitterLabel={shareReviewPhotos.length === 1 ? uploaderName(photos.find((p) => p.id === shareReviewPhotoIds[0])?.uploaded_by || null) : `${shareReviewPhotos.length} photos selected`}
        confirmLabel="Share with Client"
        cancelLabel="Cancel / Edit"
        loading={bulkLoading}
        onCancel={() => {
          setShareReviewOpen(false);
          setShareReviewPhotoIds([]);
          setShareReviewNote('');
        }}
        onConfirm={() => void confirmClientShareReview()}
      />

      {prepareClientOpen && prepareClientContext && user && (
        <PrepareDailyLogForClientModal
          open={prepareClientOpen}
          projectId={prepareClientContext.projectId}
          projectName={prepareClientContext.projectName}
          logDate={prepareClientContext.logDate}
          dateLabel={prepareClientContext.dateLabel}
          photos={prepareClientContext.photos}
          pmUserId={user.id}
          onClose={() => {
            setPrepareClientOpen(false);
            setPrepareClientContext(null);
          }}
          onSuccess={() => {
            setToast({ message: 'Daily log prepared for client', type: 'success' });
            refresh();
          }}
          onPhotosChanged={refresh}
        />
      )}
    </MgmtLayout>
  );
}